/**
 * Firebase authentication context and provider.
 *
 * Wraps the app with Google sign-in support via Firebase Auth and listens for
 * real-time subscription status changes in the Firebase Realtime Database.
 * Exposes user state, sign-in/sign-out methods, and an isPremium flag through
 * the useAuth() hook.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signOut as firebaseSignOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { ref, onValue, off } from 'firebase/database';
import { auth, googleProvider, database } from './firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Listen for subscription changes when user is authenticated
  useEffect(() => {
    if (!user?.uid) {
      setSubscription(null);
      return;
    }

    const subscriptionRef = ref(database, `users/${user.uid}/subscription`);
    
    const unsubscribe = onValue(subscriptionRef, (snapshot) => {
      const subscriptionData = snapshot.val();
      setSubscription(subscriptionData);
    });

    return () => off(subscriptionRef, 'value', unsubscribe);
  }, [user?.uid]);

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    subscription,
    signInWithGoogle,
    signOut,
    isAuthenticated: !!user,
    isPremium: subscription?.isPremium || false
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};